// src/config/clientFormConfig.js
// ============================================================
// 🔥 V1.0 ENTERPRISE — Single Source of Truth para Clientes
// ============================================================
// 🏆 Arquitectura: Salesforce / HubSpot / Monday.com level
// ✅ Fuente única para: defaults, campos, opciones, validación
// ✅ Usado por: ClientFormQuick, ClientFormDetailed, ClientEdit
// ✅ Coherencia garantizada en TODO el sistema
// ✅ Sin ruptura de código existente
// ============================================================

// ============================================================
// 🎯 OPCIONES UNIFICADAS
// ============================================================
export const CLIENT_OPTIONS = {
  type: [
    { value: "Comprador", label: "Comprador", icon: "ShoppingCart", color: "blue" },
    { value: "Vendedor", label: "Vendedor", icon: "Home", color: "green" },
    { value: "Investidor", label: "Investidor", icon: "TrendingUp", color: "purple" },
    { value: "Locatário", label: "Locatário", icon: "Key", color: "orange" }
  ],

  status: [
    { value: "Ativo", label: "Ativo", color: "emerald" },
    { value: "Inativo", label: "Inativo", color: "slate" },
    { value: "Lead", label: "Lead", color: "blue" },
    { value: "Arquivado", label: "Arquivado", color: "gray" }
  ],

  pipeline: [
    { value: "Lead Novo", label: "Lead Novo", color: "blue", description: "Acabou de entrar no funil" },
    { value: "Em Atendimento", label: "Em Atendimento", color: "yellow", description: "Em contato ativo" },
    { value: "Proposta Enviada", label: "Proposta Enviada", color: "purple", description: "Proposta em análise" },
    { value: "Fechado", label: "Fechado", color: "emerald", description: "Negócio concluído" },
    { value: "Perdido", label: "Perdido", color: "rose", description: "Não fechou" }
  ],

  temperature: [
    { value: "Quente", label: "🔥 Quente", color: "rose", description: "Alta probabilidade de fechar" },
    { value: "Morno", label: "🌤️ Morno", color: "amber", description: "Interesse moderado" },
    { value: "Frio", label: "❄️ Frio", color: "sky", description: "Baixa probabilidade" }
  ],

  source: [
    { value: "Direto", label: "Direto", icon: "User" },
    { value: "Indicação", label: "Indicação", icon: "Users" },
    { value: "Site", label: "Site", icon: "Globe" },
    { value: "WhatsApp", label: "WhatsApp", icon: "MessageCircle" },
    { value: "Instagram", label: "Instagram", icon: "Instagram" },
    { value: "Facebook", label: "Facebook", icon: "Facebook" },
    { value: "OLX", label: "OLX", icon: "ShoppingBag" },
    { value: "Zap Imóveis", label: "Zap Imóveis", icon: "Home" },
    { value: "Google", label: "Google Ads", icon: "Search" },
    { value: "Feira", label: "Feira/Evento", icon: "Calendar" },
    { value: "Outros", label: "Outros", icon: "MoreHorizontal" }
  ],

  preferredContact: [
    { value: "whatsapp", label: "WhatsApp", icon: "MessageCircle" },
    { value: "phone", label: "Telefone", icon: "Phone" },
    { value: "email", label: "E-mail", icon: "Mail" },
    { value: "in_person", label: "Presencial", icon: "User" }
  ],

  bestTimeToContact: [
    { value: "any", label: "Qualquer horário" },
    { value: "morning", label: "Manhã (8h-12h)" },
    { value: "afternoon", label: "Tarde (13h-17h)" },
    { value: "evening", label: "Noite (18h-21h)" }
  ],

  propertyType: [
    { value: "apartamento", label: "Apartamento" },
    { value: "casa", label: "Casa" },
    { value: "terreno", label: "Terreno" },
    { value: "comercial", label: "Comercial" },
    { value: "rural", label: "Rural" },
    { value: "cobertura", label: "Cobertura" },
    { value: "studio", label: "Studio/Kitnet" }
  ]
};

// ============================================================
// 📋 VALORES POR DEFECTO UNIFICADOS
// ============================================================
export const CLIENT_DEFAULTS = {
  name: "",
  email: "",
  phone: "",
  type: "Comprador",
  status: "Ativo",
  pipeline: "Lead Novo",
  temperature: "Morno",
  source: "Direto",
  budget: "",
  expectedValue: "",
  tags: [],
  document: "",
  birthDate: "",
  address: "",
  city: "",
  state: "",
  zipCode: "",
  notes: "",
  secondaryPhone: "",
  secondaryEmail: "",
  preferredContact: "whatsapp",
  bestTimeToContact: "any",
  propertyType: "",
  desiredNeighborhood: "",
  bedrooms: "",
  parkingSpots: "",
  areaRange: "",
  score: 0,
  createdAt: "",
  updatedAt: ""
};

// ============================================================
// 🔧 DEFINICIÓN DE CAMPOS
// ============================================================
export const CLIENT_FIELDS = {
  // Básicos (Modal rápido + todos los forms)
  name: {
    key: "name",
    label: "Nome completo",
    placeholder: "Digite o nome completo",
    type: "text",
    required: true,
    icon: "User",
    grid: "full",
    section: "basic",
    order: 1,
    validation: {
      required: "Nome é obrigatório",
      minLength: { value: 2, message: "Nome deve ter pelo menos 2 caracteres" },
      maxLength: { value: 120, message: "Nome deve ter no máximo 120 caracteres" }
    }
  },

  email: {
    key: "email",
    label: "E-mail",
    placeholder: "cliente@email.com",
    type: "email",
    required: false,
    icon: "Mail",
    grid: "half",
    section: "basic",
    order: 2,
    validation: {
      pattern: { 
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, 
        message: "E-mail inválido" 
      }
    }
  },

  phone: {
    key: "phone",
    label: "Telefone",
    placeholder: "(11) 99999-9999",
    type: "tel",
    required: false,
    icon: "Phone",
    grid: "half",
    section: "basic",
    order: 3,
    mask: "phone",
    validation: {
      minLength: { value: 10, message: "Telefone deve ter pelo menos 10 dígitos" }
    }
  },

  type: {
    key: "type",
    label: "Tipo de cliente",
    type: "select",
    required: true,
    icon: "Users",
    grid: "half",
    section: "basic",
    order: 4,
    options: "type",
    validation: {
      required: "Tipo é obrigatório"
    }
  },

  budget: {
    key: "budget",
    label: "Orçamento",
    placeholder: "R$ 500.000",
    type: "currency",
    required: false,
    icon: "DollarSign",
    grid: "half",
    section: "basic",
    order: 5
  },

  // Pipeline y Status
  pipeline: {
    key: "pipeline",
    label: "Pipeline",
    type: "select",
    required: true,
    icon: "Target",
    grid: "half",
    section: "pipeline",
    order: 6,
    options: "pipeline",
    validation: {
      required: "Pipeline é obrigatório"
    }
  },

  temperature: {
    key: "temperature",
    label: "Temperatura",
    type: "select",
    required: true,
    icon: "Thermometer",
    grid: "half",
    section: "pipeline",
    order: 7,
    options: "temperature",
    validation: {
      required: "Temperatura é obrigatória"
    }
  },

  status: {
    key: "status",
    label: "Status",
    type: "select",
    required: true,
    icon: "Activity",
    grid: "half",
    section: "pipeline",
    order: 8,
    options: "status",
    validation: {
      required: "Status é obrigatório"
    }
  },

  source: {
    key: "source",
    label: "Origem do lead",
    type: "select",
    required: false,
    icon: "Link",
    grid: "half",
    section: "pipeline",
    order: 9,
    options: "source"
  },

  expectedValue: {
    key: "expectedValue",
    label: "Valor esperado",
    placeholder: "R$ 350.000",
    type: "currency",
    required: false,
    icon: "TrendingUp",
    grid: "half",
    section: "pipeline",
    order: 10
  },

  score: {
    key: "score",
    label: "Score inicial",
    type: "range",
    required: false,
    icon: "Star",
    grid: "half",
    section: "pipeline",
    order: 11,
    min: 0,
    max: 100,
    step: 5
  },

  // Contacto adicional
  secondaryPhone: {
    key: "secondaryPhone",
    label: "Telefone secundário",
    placeholder: "(11) 99999-9999",
    type: "tel",
    required: false,
    icon: "Phone",
    grid: "half",
    section: "contact",
    order: 12,
    mask: "phone"
  },

  secondaryEmail: {
    key: "secondaryEmail",
    label: "E-mail secundário",
    placeholder: "secundario@email.com",
    type: "email",
    required: false,
    icon: "Mail",
    grid: "half",
    section: "contact",
    order: 13
  },

  preferredContact: {
    key: "preferredContact",
    label: "Contato preferido",
    type: "select",
    required: false,
    icon: "MessageCircle",
    grid: "half",
    section: "contact",
    order: 14,
    options: "preferredContact"
  },

  bestTimeToContact: {
    key: "bestTimeToContact",
    label: "Melhor horário",
    type: "select",
    required: false,
    icon: "Clock",
    grid: "half",
    section: "contact",
    order: 15,
    options: "bestTimeToContact"
  },

  // Documentos
  document: {
    key: "document",
    label: "CPF/CNPJ",
    placeholder: "000.000.000-00",
    type: "text",
    required: false,
    icon: "FileText",
    grid: "half",
    section: "documents",
    order: 16,
    mask: "cpf"
  },

  birthDate: {
    key: "birthDate",
    label: "Data de nascimento",
    type: "date",
    required: false,
    icon: "Calendar",
    grid: "half",
    section: "documents",
    order: 17
  },

  // Dirección
  address: {
    key: "address",
    label: "Endereço",
    placeholder: "Rua, número, bairro",
    type: "text",
    required: false,
    icon: "MapPin",
    grid: "full",
    section: "address",
    order: 18
  },

  city: {
    key: "city",
    label: "Cidade",
    placeholder: "Cidade",
    type: "text",
    required: false,
    icon: "Building2",
    grid: "third",
    section: "address",
    order: 19
  },

  state: {
    key: "state",
    label: "Estado",
    type: "select",
    required: false,
    icon: "MapPin",
    grid: "third",
    section: "address",
    order: 20,
    options: [
      "AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG",
      "PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"
    ].map(uf => ({ value: uf, label: uf }))
  },

  zipCode: {
    key: "zipCode",
    label: "CEP",
    placeholder: "00000-000",
    type: "text",
    required: false,
    icon: "MapPin",
    grid: "third",
    section: "address",
    order: 21,
    mask: "cep"
  },

  // Intereses
  propertyType: {
    key: "propertyType",
    label: "Tipo de imóvel",
    type: "select",
    required: false,
    icon: "Home",
    grid: "half",
    section: "interest",
    order: 22,
    options: "propertyType"
  },

  desiredNeighborhood: {
    key: "desiredNeighborhood",
    label: "Bairro desejado",
    placeholder: "Bairro de interesse",
    type: "text",
    required: false,
    icon: "MapPin",
    grid: "half",
    section: "interest",
    order: 23
  },

  bedrooms: {
    key: "bedrooms",
    label: "Quartos",
    type: "number",
    required: false,
    icon: "Home",
    grid: "third",
    section: "interest",
    order: 24,
    min: 0,
    max: 10
  },

  parkingSpots: {
    key: "parkingSpots",
    label: "Vagas",
    type: "number",
    required: false,
    icon: "Car",
    grid: "third",
    section: "interest",
    order: 25,
    min: 0,
    max: 10
  },

  areaRange: {
    key: "areaRange",
    label: "Área útil (m²)",
    placeholder: "50-100",
    type: "text",
    required: false,
    icon: "Maximize2",
    grid: "third",
    section: "interest",
    order: 26
  },

  // Tags y notas
  tags: {
    key: "tags",
    label: "Tags",
    type: "tags",
    required: false,
    icon: "Tag",
    grid: "full",
    section: "extra",
    order: 27,
    suggestions: [
      "VIP", "Urgente", "Investidor", "Primeira compra",
      "Financiamento", "À vista", "Permuta", "CNPJ"
    ]
  },

  notes: {
    key: "notes",
    label: "Observações",
    placeholder: "Anotações sobre o cliente...",
    type: "textarea",
    required: false,
    icon: "FileText",
    grid: "full",
    section: "extra",
    order: 28,
    rows: 3
  }
};

// ============================================================
// 📊 NIVELES DE FORMULARIO
// ============================================================
export const CLIENT_FORM_LEVELS = {
  // Modal rápido (5 campos esenciales)
  quick: {
    title: "Novo Cliente",
    description: "Cadastre um lead em segundos.",
    fields: ["name", "email", "phone", "type", "budget"],
    sections: ["basic"],
    submitLabel: "Criar Cliente",
    cancelLabel: "Cancelar"
  },

  // Formulario detallado (sidebar - todos los campos)
  detailed: {
    title: "Cadastro Completo",
    description: "Preencha todas as informações do cliente.",
    fields: "all",
    sections: [
      { key: "basic", title: "Informações Básicas", icon: "User" },
      { key: "pipeline", title: "Status e Pipeline", icon: "Target" },
      { key: "contact", title: "Contato Adicional", icon: "MessageCircle" },
      { key: "documents", title: "Documentos", icon: "FileText" },
      { key: "address", title: "Endereço", icon: "MapPin" },
      { key: "interest", title: "Interesses", icon: "Home" },
      { key: "extra", title: "Tags e Observações", icon: "Tag" }
    ],
    submitLabel: "Salvar Cliente",
    cancelLabel: "Cancelar"
  },

  // Edición de cliente (todos los campos + datos existentes)
  edit: {
    title: "Editar Cliente",
    description: "Atualize as informações do cliente.",
    fields: "all",
    sections: [
      { key: "basic", title: "Informações Básicas", icon: "User" },
      { key: "pipeline", title: "Status e Pipeline", icon: "Target" },
      { key: "contact", title: "Contato Adicional", icon: "MessageCircle" },
      { key: "documents", title: "Documentos", icon: "FileText" },
      { key: "address", title: "Endereço", icon: "MapPin" },
      { key: "interest", title: "Interesses", icon: "Home" },
      { key: "extra", title: "Tags e Observações", icon: "Tag" }
    ],
    submitLabel: "Salvar Alterações",
    cancelLabel: "Descartar"
  }
};

// ============================================================
// 🔒 VALIDACIÓN UNIFICADA
// ============================================================
export const validateClientField = (fieldKey, value, allValues = {}) => {
  const field = CLIENT_FIELDS[fieldKey];
  if (!field || !field.validation) return null;

  const rules = field.validation;
  const strValue = String(value || "").trim();

  // Required
  if (rules.required && !strValue) {
    return rules.required;
  }

  // Si no hay valor y no es requerido, no validar más
  if (!strValue && !rules.required) return null;

  // Min length
  if (rules.minLength && strValue.replace(/\D/g, "").length < rules.minLength.value) {
    return rules.minLength.message;
  }

  // Max length
  if (rules.maxLength && strValue.length > rules.maxLength.value) {
    return rules.maxLength.message;
  }

  // Pattern
  if (rules.pattern && !rules.pattern.value.test(strValue)) {
    return rules.pattern.message;
  }

  // Min value
  if (rules.min && Number(value) < rules.min.value) {
    return rules.min.message;
  }

  // Max value
  if (rules.max && Number(value) > rules.max.value) {
    return rules.max.message;
  }

  return null;
};

export const validateClientForm = (values, formLevel = "quick") => {
  const errors = {};
  const level = CLIENT_FORM_LEVELS[formLevel];
  
  if (!level) return errors;

  const fieldsToValidate = level.fields === "all" 
    ? Object.keys(CLIENT_FIELDS) 
    : level.fields;

  fieldsToValidate.forEach(fieldKey => {
    const error = validateClientField(fieldKey, values[fieldKey], values);
    if (error) errors[fieldKey] = error;
  });

  return errors;
};

// ============================================================
// 🛠️ HELPERS DE FORMATEO
// ============================================================
export const formatPhoneDisplay = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 0) return "";
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
};

export const formatCPFDisplay = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
};

export const formatCEPDisplay = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5, 8)}`;
};

export const formatCurrencyDisplay = (value) => {
  const num = Number(String(value || "").replace(/\D/g, "")) / 100;
  if (!num || num <= 0) return "";
  return `R$ ${num.toLocaleString("pt-BR")}`;
};

export const normalizePhoneBR = (phone = "") => {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("55")) return digits;
  if (digits.length >= 10 && digits.length <= 11) return `55${digits}`;
  return digits;
};

// ============================================================
// 🎨 GETTERS PARA UI
// ============================================================
export const getFieldOptions = (fieldKey) => {
  const field = CLIENT_FIELDS[fieldKey];
  if (!field?.options) return [];
  
  if (typeof field.options === "string") {
    return CLIENT_OPTIONS[field.options] || [];
  }
  
  return field.options;
};

export const getOptionByValue = (fieldKey, value) => {
  const options = getFieldOptions(fieldKey);
  return options.find(opt => opt.value === value || opt === value) || null;
};

export const getFieldMask = (fieldKey) => {
  const field = CLIENT_FIELDS[fieldKey];
  return field?.mask || null;
};

export const getSectionFields = (sectionKey) => {
  return Object.values(CLIENT_FIELDS)
    .filter(field => field.section === sectionKey)
    .sort((a, b) => a.order - b.order);
};

export const getFormFields = (formLevel = "quick") => {
  const level = CLIENT_FORM_LEVELS[formLevel];
  if (!level) return [];
  
  if (level.fields === "all") {
    return Object.values(CLIENT_FIELDS).sort((a, b) => a.order - b.order);
  }
  
  return level.fields
    .map(key => CLIENT_FIELDS[key])
    .filter(Boolean)
    .sort((a, b) => a.order - b.order);
};

// ============================================================
// 📤 EXPORTACIÓN POR DEFECTO
// ============================================================
export default {
  CLIENT_OPTIONS,
  CLIENT_DEFAULTS,
  CLIENT_FIELDS,
  CLIENT_FORM_LEVELS,
  validateClientField,
  validateClientForm,
  formatPhoneDisplay,
  formatCPFDisplay,
  formatCEPDisplay,
  formatCurrencyDisplay,
  normalizePhoneBR,
  getFieldOptions,
  getOptionByValue,
  getFieldMask,
  getSectionFields,
  getFormFields
};